/**
 * Tier Discount System - Black Friday Edition
 * OpenSpaces Labs - Selah Clothing
 * 
 * Dynamic tiered spending system with real-time cart tracking
 * and automatic tier unlocking based on configurable thresholds.
 */

class TierDiscountSystem {
  constructor(config) {
    this.config = config;
    this.currentCart = null;
    this.activeTiers = [];
    this.claimedTiers = this.loadClaimedTiers();
    this.selectionCounts = {}; // Track selections per tier
    this.isInitialized = false;
    
    // Sort tiers by threshold ascending
    this.config.tiers.sort((a, b) => a.threshold - b.threshold);
    
    this.init();
  }
  
  async init() {
    if (!this.config.enabled) {
      console.log('[Tier System] Disabled in settings');
      return;
    }
    
    console.log('[Tier System] Initializing...', this.config);
    
    try {
      // Load current cart
      await this.fetchCart();
      
      // Calculate active tiers
      this.calculateActiveTiers();
      
      // Setup event listeners
      this.setupEventListeners();
      
      // Update UI
      this.updateAllUI();
      
      // Handle auto-open logic
      this.handleAutoOpen();
      
      this.isInitialized = true;
      console.log('[Tier System] Initialized successfully');
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('tierSystem:ready', { 
        detail: { tiers: this.activeTiers } 
      }));
      
    } catch (error) {
      console.error('[Tier System] Initialization error:', error);
    }
  }
  
  /**
   * Fetch current cart from Shopify
   */
  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      
      this.currentCart = await response.json();
      console.log('[Tier System] Cart fetched:', this.currentCart);
      
      return this.currentCart;
    } catch (error) {
      console.error('[Tier System] Error fetching cart:', error);
      this.currentCart = { items: [], item_count: 0, total_price: 0 };
      return this.currentCart;
    }
  }
  
  /**
   * Calculate cart subtotal excluding tier reward items
   */
  calculateCartSubtotal() {
    if (!this.currentCart || !this.currentCart.items) return 0;
    
    return this.currentCart.items.reduce((total, item) => {
      // Skip items marked as tier rewards
      if (item.properties && item.properties._tier_reward === 'true') {
        return total;
      }
      // Use original_price (base currency) to avoid international pricing inflation
      // This prevents duties, taxes, and currency conversion from affecting tier thresholds
      const basePrice = item.original_price || item.price;
      return total + (basePrice * item.quantity);
    }, 0);
  }
  
  /**
   * Calculate which tiers are active/unlocked
   */
  calculateActiveTiers() {
    const subtotal = this.calculateCartSubtotal();
    this.activeTiers = [];
    
    console.log('[Tier System] Calculating tiers - Cart subtotal:', subtotal, 'cents ($' + (subtotal/100).toFixed(2) + ')');
    
    this.config.tiers.forEach((tier, index) => {
      const isUnlocked = subtotal >= tier.threshold;
      const isClaimed = this.claimedTiers.has(tier.id);
      const amountNeeded = Math.max(0, tier.threshold - subtotal);
      const progress = Math.min(100, (subtotal / tier.threshold) * 100);
      
      console.log(`[Tier System] ${tier.name}: threshold=${tier.threshold} (${tier.threshold/100}), subtotal=${subtotal}, isUnlocked=${isUnlocked}`);
      
      const status = {
        tier: tier,
        index: index,
        isUnlocked: isUnlocked,
        isClaimed: isClaimed,
        amountNeeded: amountNeeded,
        progress: progress,
        subtotal: subtotal,
        selectionsUsed: this.selectionCounts[tier.id] || 0
      };
      
      this.activeTiers.push(status);
    });
    
    console.log('[Tier System] Active tiers calculated:', this.activeTiers);
    return this.activeTiers;
  }
  
  /**
   * Get next tier that hasn't been unlocked yet
   */
  getNextTier() {
    return this.activeTiers.find(t => !t.isUnlocked);
  }
  
  /**
   * Get current unlocked tier that hasn't been fully claimed
   */
  getCurrentTier() {
    return [...this.activeTiers]
      .reverse()
      .find(t => t.isUnlocked && !t.isClaimed);
  }
  
  /**
   * Get current highest unlocked tier (for tier upgrade logic)
   */
  getCurrentHighestTier() {
    const unlockedTiers = this.activeTiers.filter(t => t.isUnlocked);
    if (unlockedTiers.length === 0) return null;
    
    // Sort by threshold descending, return highest
    return unlockedTiers.sort((a, b) => b.tier.threshold - a.tier.threshold)[0];
  }
  
  /**
   * Find original product by SKU (for inventory checking)
   * Free gift products have same SKU as original products
   */
  async findOriginalProductBySKU(freeGiftVariantId) {
    try {
      // Get the free gift variant's SKU from tier config
      let freeGiftSKU = null;
      let freeGiftProduct = null;
      
      for (const tier of this.config.tiers) {
        for (const product of tier.products) {
          const variant = product.variants.find(v => v.id == freeGiftVariantId);
          if (variant) {
            freeGiftSKU = variant.sku;
            freeGiftProduct = product;
            break;
          }
        }
        if (freeGiftSKU) break;
      }
      
      if (!freeGiftSKU) {
        console.warn('[Tier System] Could not find SKU for variant:', freeGiftVariantId);
        return null;
      }
      
      console.log('[Tier System] Searching for original product with SKU:', freeGiftSKU);
      
      // Search for products with this SKU via search API
      const searchResponse = await fetch(`/search?q=${encodeURIComponent(freeGiftSKU)}&type=product&view=json`);
      
      if (!searchResponse.ok) {
        console.warn('[Tier System] Search API not available, using fallback');
        return null;
      }
      
      const searchData = await searchResponse.json();
      
      // Find a product that's NOT the free gift (doesn't have "Hidden Gift" tag)
      const originalProduct = searchData.results?.find(p => 
        p.id !== freeGiftProduct.id && 
        !p.tags?.includes('Hidden Gift') &&
        !p.tags?.includes('hidden gift') &&
        p.available
      );
      
      if (originalProduct) {
        console.log('[Tier System] Found original product:', originalProduct.title);
        return originalProduct;
      }
      
      console.warn('[Tier System] Could not find original product for SKU:', freeGiftSKU);
      return null;
      
    } catch (error) {
      console.error('[Tier System] Error finding original product:', error);
      return null;
    }
  }
  
  /**
   * Check if original product variant is available
   */
  async checkOriginalProductAvailability(freeGiftVariantId) {
    try {
      // Find the free gift variant's SKU
      let freeGiftSKU = null;
      
      for (const tier of this.config.tiers) {
        for (const product of tier.products) {
          const variant = product.variants.find(v => v.id == freeGiftVariantId);
          if (variant) {
            freeGiftSKU = variant.sku;
            break;
          }
        }
        if (freeGiftSKU) break;
      }
      
      if (!freeGiftSKU) {
        console.warn('[Tier System] SKU not found, allowing claim (fallback)');
        return true; // Fallback: allow if we can't verify
      }
      
      // Fetch product data via API to check availability
      // We'll use the variant ID to get product info
      const productResponse = await fetch(`/products.json?limit=250`);
      
      if (!productResponse.ok) {
        console.warn('[Tier System] Products API unavailable, allowing claim (fallback)');
        return true;
      }
      
      const productsData = await productResponse.json();
      
      // Find original product (not tagged as Hidden Gift) with matching SKU
      for (const product of productsData.products) {
        // Skip free gift products
        if (product.tags?.includes('Hidden Gift') || product.tags?.includes('hidden gift')) {
          continue;
        }
        
        // Find variant with matching SKU
        const matchingVariant = product.variants.find(v => v.sku === freeGiftSKU);
        
        if (matchingVariant) {
          console.log('[Tier System] Found original variant:', {
            product: product.title,
            variant: matchingVariant.title,
            available: matchingVariant.available,
            inventory: matchingVariant.inventory_quantity
          });
          
          return matchingVariant.available;
        }
      }
      
      console.warn('[Tier System] Original product not found, allowing claim (fallback)');
      return true; // Fallback: allow if we can't find original
      
    } catch (error) {
      console.error('[Tier System] Error checking availability:', error);
      return true; // Fallback: allow on error
    }
  }
  
  /**
   * Add tier reward item to cart
   */
  async addTierReward(productVariantId, tierId, productTitle) {
    const tierInfo = this.config.tiers.find(t => t.id === tierId);
    if (!tierInfo) {
      console.error('[Tier System] Tier not found:', tierId);
      return { ok: false };
    }
    
    // Check if tier is unlocked
    const tierStatus = this.activeTiers.find(t => t.tier.id === tierId);
    if (!tierStatus || !tierStatus.isUnlocked) {
      alert('This tier is not yet unlocked. Keep shopping to reach the threshold!');
      return { ok: false };
    }
    
    // Check selection limits
    if (!tierInfo.allowMultiple) {
      const currentCount = this.selectionCounts[tierId] || 0;
      if (currentCount >= 1) {
        alert('You have already claimed your gift from this tier!');
        return { ok: false };
      }
    } else if (tierInfo.maxSelections) {
      const currentCount = this.selectionCounts[tierId] || 0;
      if (currentCount >= tierInfo.maxSelections) {
        alert(`You can only select up to ${tierInfo.maxSelections} items from this tier.`);
        return { ok: false };
      }
    }
    
    // ✅ NEW: Check if original product is in stock
    console.log('[Tier System] Checking original product availability...');
    const isOriginalAvailable = await this.checkOriginalProductAvailability(productVariantId);
    
    if (!isOriginalAvailable) {
      alert('Sorry! This item is currently out of stock.');
      console.log('[Tier System] Original product out of stock, blocking claim');
      return { ok: false };
    }
    
    console.log('[Tier System] Original product available, proceeding with add');
    console.log('[Tier System] Adding tier reward:', { productVariantId, tierId, productTitle });
    
    try {
      const formData = {
        items: [{
          id: productVariantId,
          quantity: 1,
          properties: {
            '_tier_reward': 'true',
            '_tier_id': tierId,
            '_tier_name': tierInfo.name,
            '_free_gift': '✓ FREE Black Friday Gift'
          }
        }]
      };
      
      // Add the item to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      // After adding, update the line item price to $0
      if (response.ok) {
        const addResult = await response.json();
        console.log('[Tier System] Item added:', addResult);
        
        // Now update the cart to set price to $0 via cart update
        await this.setLineItemPriceToZero(addResult.key || productVariantId);
      }
      
      if (response.ok) {
        // Increment selection count
        this.selectionCounts[tierId] = (this.selectionCounts[tierId] || 0) + 1;
        
        // Mark tier as claimed if selection limit reached
        if (!tierInfo.allowMultiple || 
            this.selectionCounts[tierId] >= tierInfo.maxSelections) {
          this.claimedTiers.add(tierId);
          this.saveClaimedTiers();
        }
        
        // Refresh cart and UI
        await this.fetchCart();
        this.calculateActiveTiers();
        this.updateAllUI();
        
        // Trigger ALL possible cart update events (comprehensive approach)
        const cartUpdateEvents = [
          'cart:updated',
          'cart:refresh',
          'cart:change',
          'ajaxCart:updated',
          'ajaxCart:refresh',
          'cart.requestComplete',
          'cart:add',
          'theme:cart:update'
        ];
        
        cartUpdateEvents.forEach(eventName => {
          document.dispatchEvent(new CustomEvent(eventName, {
            detail: { cart: this.currentCart }
          }));
        });
        
        // Try to update cart drawer/count directly if elements exist
        this.updateCartUI();
        
        // Trigger theme-specific functions
        if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        }
        if (typeof window.updateCart === 'function') {
          window.updateCart();
        }
        if (typeof window.cartDrawerRefresh === 'function') {
          window.cartDrawerRefresh();
        }
        
        // Force Udesly cart refresh if available
        if (typeof window.Udesly !== 'undefined' && window.Udesly.cart) {
          window.Udesly.cart.refresh();
        }
        
        // Show success message
        this.showSuccessMessage(productTitle, tierInfo.name);
        
        console.log('[Tier System] Reward added successfully');
        return { ok: true };
      } else {
        const errorData = await response.json();
        console.error('[Tier System] Error adding to cart:', errorData);
        alert('Error adding item to cart. Please try again.');
        return { ok: false };
      }
    } catch (error) {
      console.error('[Tier System] Exception adding reward:', error);
      alert('Error adding item to cart. Please try again.');
      return { ok: false };
    }
  }
  
  /**
   * Set a line item's price to $0 by applying a discount code
   * NOTE: This requires merchant to set up an automatic discount code for these products
   */
  async setLineItemPriceToZero(lineKey) {
    // Note: Shopify doesn't allow direct price manipulation via Cart API
    // The merchant needs to either:
    // 1. Create $0 variants of free gift products
    // 2. Set up automatic discount codes for these items
    // 3. Use Shopify Scripts (Plus only)
    console.log('[Tier System] Item added - merchant needs to configure pricing for free gifts');
    
    // Trigger cart update event
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }
  
  /**
   * Remove tier reward item from cart
   */
  async removeTierReward(lineItemKey, tierId) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          id: lineItemKey, 
          quantity: 0 
        })
      });
      
      if (response.ok) {
        // Decrement selection count
        if (this.selectionCounts[tierId] > 0) {
          this.selectionCounts[tierId]--;
        }
        
        await this.fetchCart();
        this.calculateActiveTiers();
        this.updateAllUI();
        
        console.log('[Tier System] Reward removed');
      }
    } catch (error) {
      console.error('[Tier System] Error removing reward:', error);
    }
  }
  
  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    // Listen to multiple cart update events (different themes use different events)
    document.addEventListener('cart:updated', () => {
      console.log('[Tier System] cart:updated event received');
      this.handleCartUpdate();
    });
    
    document.addEventListener('cart.requestComplete', () => {
      console.log('[Tier System] cart.requestComplete event received');
      this.handleCartUpdate();
    });
    
    // Listen for AJAX cart changes (Udesly framework)
    document.addEventListener('ajaxCart:updated', () => {
      console.log('[Tier System] ajaxCart:updated event received');
      this.handleCartUpdate();
    });
    
    // Poll cart every 3 seconds as fallback
    setInterval(() => {
      this.handleCartUpdate();
    }, 3000);
    
    // Popup trigger button
    const triggerBtn = document.querySelector('[data-tier-popup-trigger]');
    if (triggerBtn) {
      triggerBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.openPopup();
      });
    }
    
    // Close popup button
    const closeBtn = document.querySelector('[data-tier-popup-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closePopup();
      });
    }
    
    // Close on overlay click
    const overlay = document.querySelector('[data-tier-popup-overlay]');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closePopup();
        }
      });
    }
    
    // Claim buttons
    document.addEventListener('click', (e) => {
      const claimBtn = e.target.closest('[data-tier-claim-btn]');
      if (claimBtn && !claimBtn.disabled) {
        e.preventDefault();
        this.handleClaimClick(claimBtn);
      }
    });
    
    // Variant selectors
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-product-variant-select]')) {
        this.handleVariantChange(e.target);
      }
    });
    
    // Keyboard escape to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closePopup();
      }
    });
  }
  
  /**
   * Handle cart update event
   */
  async handleCartUpdate() {
    await this.fetchCart();
    this.calculateActiveTiers();
    this.updateAllUI();
    this.validateTierRewards();
  }
  
  /**
   * Validate that tier rewards are still eligible
   * Also handles tier upgrade logic - removes items from lower tiers
   */
  async validateTierRewards() {
    if (!this.currentCart || !this.currentCart.items) return;
    
    const highestTier = this.getCurrentHighestTier();
    const itemsToRemove = [];
    let upgradedFromTier = null;
    let lowerTierItemsFound = false;
    
    this.currentCart.items.forEach(item => {
      if (item.properties && item.properties._tier_reward === 'true') {
        const tierId = item.properties._tier_id;
        const tierStatus = this.activeTiers.find(t => t.tier.id === tierId);
        
        // Remove if tier is no longer unlocked
        if (!tierStatus || !tierStatus.isUnlocked) {
          console.log('[Tier System] Removing ineligible reward (tier locked):', item);
          itemsToRemove.push({ 
            key: item.key, 
            tierId: tierId, 
            reason: 'tier_locked' 
          });
        }
        
        // Remove if item is from LOWER tier than current highest
        // This implements the tier upgrade system
        if (highestTier && tierStatus && 
            tierStatus.tier.threshold < highestTier.tier.threshold) {
          console.log('[Tier System] Removing lower tier reward (tier upgraded):', item);
          itemsToRemove.push({ 
            key: item.key, 
            tierId: tierId, 
            reason: 'tier_upgraded',
            tierName: tierStatus.tier.name
          });
          upgradedFromTier = tierStatus.tier.name;
          lowerTierItemsFound = true;
        }
      }
    });
    
    // Remove ineligible items
    for (const item of itemsToRemove) {
      await this.removeTierReward(item.key, item.tierId);
    }
    
    // If customer upgraded tiers, show notification and open modal
    if (lowerTierItemsFound && highestTier) {
      const maxSelections = highestTier.tier.allowMultiple 
        ? (highestTier.tier.maxSelections || 1) 
        : 1;
      
      console.log('[Tier System] Customer upgraded to:', highestTier.tier.name);
      this.showUpgradeMessage(upgradedFromTier, highestTier.tier.name, maxSelections);
      
      // Auto-open modal after brief delay
      setTimeout(() => {
        this.openPopup();
      }, 1000);
    }
  }
  
  /**
   * Show tier upgrade notification to customer
   */
  showUpgradeMessage(oldTier, newTier, maxSelections) {
    const itemText = maxSelections === 1 ? 'item' : 'items';
    const message = `🎉 Congratulations! You've upgraded from ${oldTier} to ${newTier}!\n\n` +
                    `Your previous rewards have been removed.\n` +
                    `You can now select up to ${maxSelections} ${itemText} from ${newTier}.`;
    
    alert(message);
  }
  
  /**
   * Handle claim button click
   */
  async handleClaimClick(button) {
    const productId = button.dataset.productId;
    let variantId = button.dataset.variantId;
    const tierId = button.dataset.tierId;
    const productTitle = button.dataset.productTitle;
    
    // Get selected variant from dropdown if exists
    const variantSelect = document.querySelector(
      `[data-product-variant-select="${productId}"][data-tier-id="${tierId}"]`
    );
    if (variantSelect) {
      variantId = variantSelect.value;
    }
    
    // Disable button
    button.disabled = true;
    const originalText = button.innerHTML;
    button.innerHTML = '<span>Adding...</span>';
    
    // Add to cart
    const result = await this.addTierReward(variantId, tierId, productTitle);
    
    // Re-enable button
    button.disabled = false;
    button.innerHTML = originalText;
    
    if (result.ok) {
      // Update button state
      button.style.display = 'none';
      const addedIndicator = button.parentElement.querySelector(
        `[data-tier-product-added="${productId}"]`
      );
      if (addedIndicator) {
        addedIndicator.style.display = 'flex';
      }
    }
  }
  
  /**
   * Handle variant selector change
   */
  handleVariantChange(select) {
    const productId = select.dataset.productVariantSelect;
    const tierId = select.dataset.tierId;
    const selectedOption = select.options[select.selectedIndex];
    const isAvailable = selectedOption.dataset.available === 'true';
    
    // Check if tier is unlocked
    const tierStatus = this.activeTiers.find(t => t.tier.id === tierId);
    const isTierUnlocked = tierStatus && tierStatus.isUnlocked;
    
    // Update claim button
    const claimBtn = document.querySelector(
      `[data-tier-claim-btn][data-product-id="${productId}"][data-tier-id="${tierId}"]`
    );
    
    if (claimBtn) {
      claimBtn.dataset.variantId = select.value;
      
      // Disable if variant unavailable OR tier is locked
      claimBtn.disabled = !isAvailable || !isTierUnlocked;
      
      if (!isTierUnlocked) {
        claimBtn.querySelector('.tier-btn-text').textContent = 'Locked';
      } else if (!isAvailable) {
        claimBtn.querySelector('.tier-btn-text').textContent = 'Out of Stock';
      } else {
        claimBtn.querySelector('.tier-btn-text').textContent = 'Add Free Gift';
      }
    }
    
    // Update the product card state
    this.updateClaimButtons();
  }
  
  /**
   * Update all UI elements
   */
  updateAllUI() {
    this.updateProgressBar();
    this.updateTierItems();
    this.updateTierStatuses();
    this.updateTierVisibility(); // NEW: Handle tier display mode
    this.updateSelectionCounters();
    this.updateClaimButtons();
  }
  
  /**
   * Update progress bar
   */
  updateProgressBar() {
    const progressContainer = document.querySelector('[data-tier-progress-container]');
    if (!progressContainer) return;
    
    const fill = document.querySelector('[data-tier-progress-fill]');
    const message = document.querySelector('[data-tier-progress-message]');
    const cartTotal = document.querySelector('[data-tier-cart-total]');
    const nextThreshold = document.querySelector('[data-tier-next-threshold]');
    
    const subtotal = this.calculateCartSubtotal();
    const nextTier = this.getNextTier();
    
    // Update cart total
    if (cartTotal) {
      cartTotal.textContent = this.formatMoney(subtotal);
    }
    
    // Update progress bar and message
    if (nextTier) {
      const progress = nextTier.progress;
      if (fill) {
        fill.style.width = `${progress}%`;
      }
      
      if (nextThreshold) {
        nextThreshold.textContent = this.formatMoney(nextTier.tier.threshold);
      }
      
      if (message) {
        const amountFormatted = this.formatMoney(nextTier.amountNeeded);
        message.textContent = this.config.messages.tierLocked
          .replace('{amount}', amountFormatted);
      }
    } else {
      // All tiers unlocked
      if (fill) {
        fill.style.width = '100%';
      }
      
      if (message) {
        message.textContent = '🎉 All tiers unlocked!';
      }
      
      if (nextThreshold) {
        nextThreshold.textContent = 'MAX';
      }
    }
  }
  
  /**
   * Update tier item visual states
   */
  updateTierItems() {
    this.activeTiers.forEach(tierStatus => {
      const tierElement = document.querySelector(
        `[data-tier-id="${tierStatus.tier.id}"]`
      );
      
      if (!tierElement) return;
      
      // Update classes
      tierElement.classList.remove('tier-item--locked', 'tier-item--unlocked', 'tier-item--claimed');
      
      if (tierStatus.isClaimed) {
        tierElement.classList.add('tier-item--claimed');
      } else if (tierStatus.isUnlocked) {
        tierElement.classList.add('tier-item--unlocked');
      } else {
        tierElement.classList.add('tier-item--locked');
      }
    });
  }
  
  /**
   * Update tier status badges
   */
  updateTierStatuses() {
    this.activeTiers.forEach(tierStatus => {
      const statusBadge = document.querySelector(
        `[data-tier-status-badge="${tierStatus.tier.id}"]`
      );
      const amountNeeded = document.querySelector(
        `[data-tier-amount-needed="${tierStatus.tier.id}"]`
      );
      
      if (statusBadge) {
        // Hide all badges
        statusBadge.querySelectorAll('.tier-badge').forEach(badge => {
          badge.style.display = 'none';
        });
        
        // Show appropriate badge
        if (tierStatus.isClaimed) {
          const claimedBadge = statusBadge.querySelector('.tier-badge--claimed');
          if (claimedBadge) claimedBadge.style.display = 'flex';
        } else if (tierStatus.isUnlocked) {
          const unlockedBadge = statusBadge.querySelector('.tier-badge--unlocked');
          if (unlockedBadge) unlockedBadge.style.display = 'flex';
        } else {
          const lockedBadge = statusBadge.querySelector('.tier-badge--locked');
          if (lockedBadge) lockedBadge.style.display = 'flex';
        }
      }
      
      if (amountNeeded) {
        if (!tierStatus.isUnlocked) {
          const amountText = amountNeeded.querySelector('.tier-amount-needed-text');
          if (amountText) {
            amountText.textContent = `Spend ${this.formatMoney(tierStatus.amountNeeded)} more`;
          }
          amountNeeded.style.display = 'block';
        } else {
          amountNeeded.style.display = 'none';
        }
      }
    });
  }
  
  /**
   * Update tier visibility based on display mode (highest_only vs all)
   */
  updateTierVisibility() {
    // If mode is "all", show all tiers (default behavior)
    if (this.config.tierDisplayMode === 'all') {
      // Make sure all tier elements are visible
      this.activeTiers.forEach(tierStatus => {
        const tierElement = document.querySelector(
          `[data-tier-id="${tierStatus.tier.id}"]`
        );
        if (tierElement) {
          tierElement.style.display = '';
        }
      });
      return;
    }
    
    // Mode is "highest_only" - find the highest unlocked tier
    const highestUnlockedTier = this.activeTiers
      .filter(t => t.isUnlocked)
      .sort((a, b) => b.tier.threshold - a.tier.threshold)[0];
    
    if (!highestUnlockedTier) {
      // No tiers unlocked yet - show all tiers so they can see what's coming
      this.activeTiers.forEach(tierStatus => {
        const tierElement = document.querySelector(
          `[data-tier-id="${tierStatus.tier.id}"]`
        );
        if (tierElement) {
          tierElement.style.display = '';
        }
      });
      return;
    }
    
    // Hide all tiers except the highest unlocked one
    this.activeTiers.forEach(tierStatus => {
      const tierElement = document.querySelector(
        `[data-tier-id="${tierStatus.tier.id}"]`
      );
      
      if (!tierElement) return;
      
      // Show only the highest unlocked tier
      if (tierStatus.tier.id === highestUnlockedTier.tier.id) {
        tierElement.style.display = '';
        console.log(`[Tier System] Showing highest tier: ${tierStatus.tier.name}`);
      } else {
        tierElement.style.display = 'none';
        console.log(`[Tier System] Hiding tier: ${tierStatus.tier.name}`);
      }
    });
  }
  
  /**
   * Update selection counters
   */
  updateSelectionCounters() {
    this.activeTiers.forEach(tierStatus => {
      const counter = document.querySelector(
        `[data-tier-counter-current="${tierStatus.tier.id}"]`
      );
      
      if (counter) {
        counter.textContent = this.selectionCounts[tierStatus.tier.id] || 0;
      }
    });
  }
  
  /**
   * Update claim buttons based on tier unlock status
   */
  updateClaimButtons() {
    this.activeTiers.forEach(tierStatus => {
      // Find all claim buttons for this tier
      const claimButtons = document.querySelectorAll(
        `[data-tier-claim-btn][data-tier-id="${tierStatus.tier.id}"]`
      );
      
      claimButtons.forEach(btn => {
        const productCard = btn.closest('.tier-product-card');
        const isProductAvailable = btn.getAttribute('data-product-id') && 
          !btn.hasAttribute('disabled') || 
          btn.getAttribute('data-product-id');
        
        if (!tierStatus.isUnlocked) {
          // Tier is locked - disable button and card
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          if (productCard) {
            productCard.classList.add('tier-product-card--locked');
            productCard.style.pointerEvents = 'none';
            productCard.style.opacity = '0.6';
          }
        } else {
          // Tier is unlocked - enable button (if product is available)
          const variantId = btn.getAttribute('data-variant-id');
          const variantSelect = document.querySelector(
            `[data-product-variant-select="${btn.getAttribute('data-product-id')}"]`
          );
          
          // Check if selected variant is available
          let isVariantAvailable = true;
          if (variantSelect) {
            const selectedOption = variantSelect.options[variantSelect.selectedIndex];
            isVariantAvailable = selectedOption && 
              selectedOption.getAttribute('data-available') === 'true';
          }
          
          // Only enable if product and variant are available
          if (isProductAvailable && isVariantAvailable) {
            btn.disabled = false;
            btn.removeAttribute('aria-disabled');
            if (productCard) {
              productCard.classList.remove('tier-product-card--locked');
              productCard.style.pointerEvents = '';
              productCard.style.opacity = '';
            }
          }
        }
      });
    });
  }
  
  /**
   * Open popup modal
   */
  openPopup() {
    const overlay = document.querySelector('[data-tier-popup-overlay]');
    if (overlay) {
      overlay.classList.add('active');
      overlay.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      
      console.log('[Tier System] Popup opened');
      
      // Dispatch event
      document.dispatchEvent(new CustomEvent('tierSystem:popupOpened'));
    }
  }
  
  /**
   * Update cart UI elements directly (cart count, drawer, etc.)
   */
  updateCartUI() {
    if (!this.currentCart) return;
    
    // Update cart count badge
    const cartCountElements = document.querySelectorAll(
      '[data-cart-count], .cart-count, .cart__count, #cart-count, .header__cart-count, [data-cart-item-count]'
    );
    
    const totalItems = this.currentCart.item_count || 0;
    cartCountElements.forEach(el => {
      el.textContent = totalItems;
      if (el.style) {
        el.style.display = totalItems > 0 ? '' : 'none';
      }
    });
    
    // Update cart total
    const cartTotalElements = document.querySelectorAll(
      '[data-cart-total], .cart-total, .cart__total, #cart-total, [data-cart-price]'
    );
    
    const cartTotal = this.currentCart.total_price || 0;
    cartTotalElements.forEach(el => {
      if (el.textContent !== undefined) {
        el.textContent = this.formatMoney(cartTotal);
      }
    });
    
    // Try to refresh cart drawer if it exists
    setTimeout(() => {
      const cartDrawer = document.querySelector('[data-cart-drawer], .cart-drawer, #cart-drawer, [data-drawer="cart"], .drawer__cart');
      
      if (cartDrawer) {
        // Try to refresh cart drawer
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            // If theme has a cart drawer refresh function, call it
            if (typeof window.renderCartDrawer === 'function') {
              window.renderCartDrawer(cart);
            }
            // Trigger cart drawer refresh event
            document.dispatchEvent(new CustomEvent('cart:drawer:refresh', { detail: { cart } }));
          })
          .catch(err => console.error('[Tier System] Error updating cart drawer:', err));
        
        // Try fetching full cart page HTML for drawer update
        fetch('/cart')
          .then(res => res.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newCartDrawer = doc.querySelector('[data-cart-drawer], .cart-drawer, [data-drawer="cart"], .drawer__cart');
            
            if (newCartDrawer && cartDrawer) {
              cartDrawer.innerHTML = newCartDrawer.innerHTML;
              // Trigger any initialization scripts
              document.dispatchEvent(new CustomEvent('cart:drawer:refreshed'));
              console.log('[Tier System] Cart drawer HTML updated');
            }
          })
          .catch(err => console.error('[Tier System] Error refreshing cart drawer HTML:', err));
      } else {
        console.log('[Tier System] No cart drawer found - will refresh on modal close');
      }
    }, 100);
    
    // Trigger any jQuery events if jQuery is available
    if (typeof jQuery !== 'undefined') {
      jQuery(document).trigger('cart:updated', [this.currentCart]);
      jQuery(document).trigger('ajaxCart:updated', [this.currentCart]);
    }
  }
  
  /**
   * Close popup modal
   */
  closePopup() {
    const overlay = document.querySelector('[data-tier-popup-overlay]');
    if (overlay) {
      overlay.classList.remove('active');
      setTimeout(() => {
        overlay.style.display = 'none';
      }, 300);
      document.body.style.overflow = '';
      
      console.log('[Tier System] Popup closed - refreshing page to show updated cart');
      
      // Dispatch event
      document.dispatchEvent(new CustomEvent('tierSystem:popupClosed'));
      
      // Reload page to ensure cart shows updated items
      setTimeout(() => {
        window.location.reload();
      }, 350);
    }
  }
  
  /**
   * Handle auto-open logic
   */
  handleAutoOpen() {
    if (!this.config.autoOpen) return;
    
    const trigger = this.config.popupTrigger;
    
    if (trigger === 'page_load') {
      setTimeout(() => this.openPopup(), 1000);
    } else if (trigger === 'tier_reached') {
      const currentTier = this.getCurrentTier();
      if (currentTier && !currentTier.isClaimed) {
        this.openPopup();
      }
    }
  }
  
  /**
   * Show success message
   */
  showSuccessMessage(productTitle, tierName) {
    // You can customize this with a toast notification system
    alert(`✓ ${productTitle} added from ${tierName}!`);
  }
  
  /**
   * Format money (cents to currency)
   */
  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }
  
  /**
   * Load claimed tiers from sessionStorage
   */
  loadClaimedTiers() {
    try {
      const saved = sessionStorage.getItem('tier_claimed');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (error) {
      return new Set();
    }
  }
  
  /**
   * Save claimed tiers to sessionStorage
   */
  saveClaimedTiers() {
    try {
      sessionStorage.setItem('tier_claimed', JSON.stringify([...this.claimedTiers]));
    } catch (error) {
      console.error('[Tier System] Error saving claimed tiers:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTierSystem);
} else {
  initTierSystem();
}

function initTierSystem() {
  if (window.tierConfig && window.tierConfig.enabled) {
    window.tierSystem = new TierDiscountSystem(window.tierConfig);
  } else {
    console.log('[Tier System] Not enabled or config not found');
  }
}

